<script setup>
import { reactive, computed, onMounted, onBeforeUnmount, onUnmounted, watch, ref, nextTick } from 'vue'

import { useGlobalStore } from '@/stores/useGlobalStore'
import { useCardStore } from '@/stores/useCardStore'
import { useConnectionStore } from '@/stores/useConnectionStore'
import { useBoxStore } from '@/stores/useBoxStore'
import { useLineStore } from '@/stores/useLineStore'
import { useListStore } from '@/stores/useListStore'
import { useUserStore } from '@/stores/useUserStore'
import { useSpaceStore } from '@/stores/useSpaceStore'

import consts from '@/consts.js'
import utils from '@/utils.js'
import MoveOrCopyItems from '@/components/dialogs/MoveOrCopyItems.vue'
import CardActions from '@/components/subsections/CardActions.vue'
import BoxActions from '@/components/subsections/BoxActions.vue'
import ConnectionActions from '@/components/subsections/ConnectionActions.vue'
import ListActions from '@/components/subsections/ListActions.vue'
import AlignAndDistribute from '@/components/AlignAndDistribute.vue'
import ItemDetailsCheckboxButton from '@/components/ItemDetailsCheckboxButton.vue'

import { nanoid } from 'nanoid'
import last from 'lodash-es/last'
import { colord, extend } from 'colord'
import mixPlugin from 'colord/plugins/mix'

const globalStore = useGlobalStore()
const cardStore = useCardStore()
const connectionStore = useConnectionStore()
const boxStore = useBoxStore()
const lineStore = useLineStore()
const listStore = useListStore()
const userStore = useUserStore()
const spaceStore = useSpaceStore()

const dialogElement = ref(null)

let prevCards, prevBoxes

let unsubscribes

onMounted(() => {
  const globalActionUnsubscribe = globalStore.$onAction(
    ({ name, args }) => {
      if (name === 'triggerSelectedCardsContainInBox') {
        containItemsInNewBox()
      }
    }
  )
  unsubscribes = () => {
    globalActionUnsubscribe()
  }
})
onBeforeUnmount(() => {
  unsubscribes()
})

const state = reactive({
  copyItemsIsVisible: false,
  moveItemsIsVisible: false,
  cardsIsConnected: false,
  isPointerDown: false
})

const closeAllDialogs = () => {
  globalStore.closeAllDialogs()
}
const closeDialogs = () => {
  state.copyItemsIsVisible = false
  state.moveItemsIsVisible = false
  globalStore.triggerCloseChildDialogs()
}
const multipleConnectionsSelectedIds = computed(() => globalStore.multipleConnectionsSelectedIds)
const multipleCardsSelectedIds = computed(() => globalStore.multipleCardsSelectedIds)
const multipleBoxesSelectedIds = computed(() => globalStore.multipleBoxesSelectedIds)
const multipleLinesSelectedIds = computed(() => globalStore.multipleLinesSelectedIds)
const multipleListsSelectedIds = computed(() => globalStore.multipleListsSelectedIds)

const visible = computed(() => {
  const isSelectedItems = globalStore.getIsMultipleItemsSelected
  return globalStore.multipleSelectedActionsIsVisible && isSelectedItems
})
watch(() => visible.value, async (value, prevValue) => {
  if (value) {
    await nextTick()
    updatePositionClamped()
    closeDialogs()
    globalStore.shouldExplicitlyHideFooter = true
  } else {
    globalStore.shouldExplicitlyHideFooter = false
  }
})

const isThemeDarkAndUserColorLight = computed(() => {
  const isThemeDark = userStore.theme === 'dark'
  const userColorIsLight = !utils.colorIsDark(userColor.value)
  return isThemeDark && userColorIsLight
})
const classes = computed(() => {
  const value = utils.colorClasses({ backgroundColor: userColor.value })
  if (globalStore.currentUserIsDraggingMultipleSelectedActionsDialog) {
    value.push('is-dragging')
  }
  if (state.isPointerDown) {
    value.push('is-pointer-down')
  }
  return value
})
const maxCardCharacterLimit = computed(() => consts.cardCharacterLimit)
const userColor = computed(() => userStore.color)
const spaceZoomDecimal = computed(() => globalStore.getSpaceZoomDecimal)
const selectedItems = computed(() => spaceStore.getSpaceSelectedItems)
const cardOrBoxIsSelected = computed(() => cards.value.length || boxes.value.length)
const cardBoxOrListIsSelected = computed(() => cardOrBoxIsSelected.value || lists.value.length)
const ItemIsSelected = computed(() => Boolean(utils.countArrayItems(selectedItems.value)))

// position

const scrollIntoView = () => {
  const element = dialogElement.value
  globalStore.scrollElementIntoView({ element })
}
const updatePositionClamped = () => {
  let { x, y } = globalStore.multipleSelectedActionsPosition
  const element = dialogElement.value
  const rect = element.getBoundingClientRect()
  const { height, width } = rect
  const margin = 16
  // position is stored in space coords, dialog is rendered in page coords
  const zoom = spaceZoomDecimal.value || 1
  const offset = globalStore.spaceZoomOffset
  const maxX = (window.scrollX + globalStore.viewportWidth - width - margin - offset.x) / zoom
  const maxY = (window.scrollY + globalStore.viewportHeight - height - margin - offset.y) / zoom
  if (x < 0) {
    x = 0
  } else if (x > maxX) {
    x = maxX
  }
  if (y < 0) {
    y = 0
  } else if (y > maxY) {
    y = maxY
  }
  globalStore.multipleSelectedActionsPosition = { x, y }
}

// items

const isSpaceMember = computed(() => userStore.getUserIsSpaceMember)
const canEditAsNonMember = computed(() => {
  const spaceIsOpen = spaceStore.privacy === 'open'
  const isSpaceMember = userStore.getUserIsSpaceMember
  return spaceIsOpen && !isSpaceMember
})
const canEditAll = computed(() => {
  if (isSpaceMember.value) {
    return {
      cards: true,
      connections: true,
      boxes: true,
      lines: true,
      lists: true,
      all: true
    }
  }
  const cards = multipleCardsSelectedIds.value.length === numberOfSelectedItemsCreatedByCurrentUser.value.cards
  const connections = multipleConnectionsSelectedIds.value.length === numberOfSelectedItemsCreatedByCurrentUser.value.connections
  const boxes = multipleBoxesSelectedIds.value.length === numberOfSelectedItemsCreatedByCurrentUser.value.boxes
  const lines = userStore.getUserIsSpaceMember
  const lists = userStore.getUserIsSpaceMember
  const all = cards && connections && boxes && lines && lists
  return { cards, connections, boxes, lines, lists, all }
})
const alignableItemsIsSelected = computed(() => {
  let cardIds = multipleCardsSelectedIds.value
  const boxIds = multipleBoxesSelectedIds.value
  const listIds = multipleListsSelectedIds.value
  cardIds = cardIds.filter(id => {
    const isInSelectedList = multipleListsSelectedIds.value.includes(id)
    return !isInSelectedList
  })
  return (cardIds.length + boxIds.length + listIds.length) > 1
})
const selectedItemsIsEditableByCurrentUser = computed(() => {
  const isCards = editableCards.value.length === cards.value.length
  const isConnections = editableConnections.value.length === connections.value.length
  const isBoxes = editableBoxes.value.length === boxes.value.length
  if (isCards && isConnections && isBoxes) {
    return true
  } else {
    return false
  }
})
const numberOfSelectedItemsCreatedByCurrentUser = computed(() => {
  const connectionsCreatedByCurrentUser = connections.value?.filter(connection => {
    if (!connection) { return }
    return userStore.getItemIsCreatedByUser(connection)
  })
  const cardsCreatedByCurrentUser = cards.value?.filter(card => {
    if (!card) { return }
    return userStore.getUserIsCardCreator(card)
  })
  const boxesCreatedByCurrentUser = boxes.value?.filter(box => {
    if (!box) { return }
    userStore.getUserIsBoxCreator(box)
  })
  return {
    connections: connectionsCreatedByCurrentUser.length,
    cards: cardsCreatedByCurrentUser.length,
    boxes: boxesCreatedByCurrentUser.length
  }
})
const multipleItemsSelectedIds = computed(() => multipleCardsSelectedIds.value.concat(multipleBoxesSelectedIds.value))
const multipleItemsIsSelected = computed(() => {
  return multipleItemsSelectedIds.value.length > 1
})
const itemsIsConnectedTogether = computed(() => {
  const itemIds = multipleItemsSelectedIds.value
  const connections = connectionStore.getConnectionsByItemIds(itemIds)
  const isConnected = connections.some(connection => {
    const isStart = itemIds.includes(connection.startItemId)
    const isEnd = itemIds.includes(connection.endItemId)
    return isStart && isEnd
  })
  return isConnected
})
const backgroundColor = computed(() => {
  let color = userColor.value
  if (isThemeDarkAndUserColorLight.value) {
    color = colord(color).mix('rgba(0,0,0,0.3)').toHex() // dark tinted
  } else {
    color = colord(color).alpha(0.9).toRgbString()
  }
  return color
})
const styles = computed(() => {
  const position = globalStore.getSpacePositionInPage(globalStore.multipleSelectedActionsPosition)
  return {
    backgroundColor: backgroundColor.value,
    left: position.x + 'px',
    top: position.y + 'px'
  }
})

// cards

const cardCanBeSplit = computed(() => {
  if (!oneCardIsSelected.value) { return }
  if (!cards.value.length) { return }
  if (!cards.value[0].name) { return }
  return cards.value[0].name.includes('\n')
})
const oneCardIsSelected = computed(() => multipleCardsSelectedIds.value.length === 1)
const cardsIsSelected = computed(() => multipleCardsSelectedIds.value.length > 0)
const multipleCardsIsSelected = computed(() => multipleCardsSelectedIds.value.length > 1)
const cards = computed(() => {
  let cards = multipleCardsSelectedIds.value.map(cardId => {
    return cardStore.getCard(cardId)
  })
  cards = cards.filter(card => Boolean(card))
  prevCards = cards
  return cards
})
const editableCards = computed(() => {
  if (isSpaceMember.value) {
    return cards.value
  } else {
    return cards.value.filter(card => {
      return userStore.getUserIsCardCreator(card)
    })
  }
})

// connect

const toggleConnectItems = (event) => {
  if (itemsIsConnectedTogether.value) {
    disconnectItems()
  } else {
    connectItems(event)
  }
}
const connectItems = (event) => {
  const itemIds = multipleItemsSelectedIds.value
  itemIds.forEach((itemId, index) => {
    if (index + 1 < itemIds.length) { // create connections for middle items
      const startItemId = itemId
      const endItemId = itemIds[index + 1]
      const id = nanoid()
      const path = connectionStore.getConnectionPathBetweenItems({
        startItemId,
        endItemId,
        controlPoint: consts.straightLineConnectionPathControlPoint
      })
      connectionStore.createConnection({ id, startItemId, endItemId, path })
    }
  })
}
const disconnectItems = () => {
  const itemIds = multipleItemsSelectedIds.value
  let connections = connectionStore.getConnectionsByItemIds(itemIds)
  connections = connections.filter(connection => {
    const isStart = itemIds.includes(connection.startItemId)
    const isEnd = itemIds.includes(connection.endItemId)
    return isStart && isEnd
  })
  const connectionIds = connections.map(connection => connection.id)
  connectionStore.removeConnections(connectionIds)
}

// surround with box

const containItemsInNewBox = async () => {
  const items = cards.value.concat(boxes.value, lists.value)
  const rect = utils.boundaryRectFromItems(items)
  const padding = consts.spaceBetweenCards
  const paddingTop = 30 + padding
  const box = {
    id: nanoid(),
    x: rect.x - padding,
    y: rect.y - paddingTop,
    resizeWidth: rect.width + (padding * 2),
    resizeHeight: rect.height + (padding + paddingTop)
  }
  boxStore.createBox(box)
  globalStore.closeAllDialogs()
  await nextTick()
  await nextTick()
  globalStore.updateBoxDetailsIsVisibleForBoxId(box.id)
}

// connections

const moreLineOptionsLabel = computed(() => 'LINE')
const onlyConnectionsIsSelected = computed(() => connectionsIsSelected.value && !cardsIsSelected.value && !boxesIsSelected.value && !listsIsSelected.value)
const connectionsIsSelected = computed(() => Boolean(multipleConnectionsSelectedIds.value.length))
const connections = computed(() => {
  return multipleConnectionsSelectedIds.value.map(id => connectionStore.getConnection(id))
})
const editableConnections = computed(() => {
  if (isSpaceMember.value) {
    return connections.value
  } else {
    return connections.value.filter(connection => {
      return userStore.getItemIsCreatedByUser(connection)
    })
  }
})

// boxes

const onlyBoxesIsSelected = computed(() => boxesIsSelected.value && !cardsIsSelected.value && !connectionsIsSelected.value && !listsIsSelected.value)
const boxesIsSelected = computed(() => multipleBoxesSelectedIds.value.length > 0)
const boxes = computed(() => {
  let boxes = multipleBoxesSelectedIds.value.map(boxId => {
    return boxStore.getBox(boxId)
  })
  boxes = boxes.filter(box => Boolean(box))
  prevBoxes = boxes
  return boxes
})
const editableBoxes = computed(() => {
  if (isSpaceMember.value) {
    return boxes.value
  } else {
    return boxes.value.filter(box => {
      userStore.getUserIsBoxCreator(box)
    })
  }
})

// list

const onlyListsIsSelected = computed(() => listsIsSelected.value && !cardsIsSelected.value && !connectionsIsSelected.value && !boxesIsSelected.value)
const listsIsSelected = computed(() => multipleListsSelectedIds.value.length > 0)
const lists = computed(() => {
  let lists = multipleListsSelectedIds.value.map(listId => {
    return listStore.getList(listId)
  })
  lists = lists.filter(list => Boolean(list))
  // prevLists = lists
  return lists
})
const cardsIsInListTogether = computed(() => {
  if (!cards.value.length) { return }
  return cardStore.cardsIsInListTogether(cards)
})
const toggleListCards = async () => {
  const cards = cardsSortedByY()
  cardStore.toggleListCards(cards)
}

// split and merge

const splitCard = () => {
  // open card details and trigger the split from there
  const cardId = cards.value[0].id
  globalStore.clearMultipleSelected()
  globalStore.closeAllDialogs()
  globalStore.currentDraggingCardId = cardId
  globalStore.updateCardDetailsIsVisibleForCardId(cardId)
  globalStore.triggerSplitCard(cardId)
}
const positionNewCards = async (newCards) => {
  await nextTick()
  newCards = newCards.map((card, index) => {
    if (index === 0) { return card }
    const prevCard = newCards[index - 1]
    const rect = utils.cardElementDimensions(prevCard)
    card.y = rect.y + rect.height + consts.spaceBetweenCards
    return card
  })
  newCards = newCards.map(card => {
    return {
      id: card.id,
      name: card.name,
      y: card.y,
      width: card.width,
      height: card.height
    }
  })
  cardStore.updateCards(newCards)
  globalStore.closeAllDialogs()
}
const cardsSortedByY = () => {
  return cards.value.sort((a, b) => {
    return a.y - b.y
  })
}
const uniqueAtMentions = (mentions = []) => {
  return mentions.map(mention => {
    mention.id = nanoid()
    return mention
  })
}
const mergeSelectedCards = () => {
  let name = ''
  const cards = cardsSortedByY()
  const urlPreview = {}
  let atUserMentions = []
  let atDateMentions = []
  cards.forEach(card => {
    name = `${name}\n\n${card.name.trim()}`
    Object.keys(card).forEach(key => {
      if (key.startsWith('urlPreview') && card[key] && !urlPreview[key]) {
        urlPreview[key] = card[key]
      }
    })
    card.atUserMentions = uniqueAtMentions(card.atUserMentions)
    atUserMentions = atUserMentions.concat(card.atUserMentions)
    card.atDateMentions = uniqueAtMentions(card.atDateMentions)
    atDateMentions = atDateMentions.concat(card.atDateMentions)
  })
  name = name.trim()
  let newName
  do {
    newName = name.substring(0, maxCardCharacterLimit.value)
    const lastSpace = newName.lastIndexOf(' ')
    const lastLineBreak = newName.lastIndexOf('\n')
    const shouldSplitByMaxLength = lastSpace === -1 && lastLineBreak === -1
    if (name.length < maxCardCharacterLimit.value) {
      newName = name
      name = ''
    } else if (shouldSplitByMaxLength) {
      name = name.substring(maxCardCharacterLimit.value)
    } else if (lastSpace >= lastLineBreak) {
      newName = name.substring(0, lastSpace)
      name = name.substring(lastSpace)
    } else {
      newName = name.substring(0, lastLineBreak)
      name = name.substring(lastLineBreak)
    }
  } while (name.length > maxCardCharacterLimit.value)
  const position = { x: cards[0].x, y: cards[0].y }
  remove({ shouldRemoveCardsOnly: true })
  const cardWithBackgroundColor = cards.find(card => card.backgroundColor)
  const cardBackgroundColor = cardWithBackgroundColor?.backgroundColor
  const userCardBackgroundColor = globalStore.getNewCardColor
  const newCardId = nanoid()
  const mentions = atUserMentions.map(mention => {
    mention.id = nanoid()
    mention.cardId = newCardId
    return mention
  })
  const dateMentions = atDateMentions.map(mention => {
    mention.id = nanoid()
    mention.cardId = newCardId
    return mention
  })
  const newCard = {
    id: newCardId,
    name: newName,
    x: position.x,
    y: position.y,
    backgroundColor: cardBackgroundColor || userCardBackgroundColor,
    ...urlPreview,
    atUserMentions: mentions,
    atDateMentions: dateMentions
  }
  cardStore.createCard(newCard)
  prevCards = [newCard] // for history
  setTimeout(() => {
    positionNewCards([newCard])
  }, 100)
}

// copy and move

const toggleCopyItemsIsVisible = () => {
  const isVisible = state.copyItemsIsVisible
  closeDialogs()
  state.copyItemsIsVisible = !isVisible
}
const toggleMoveItemsIsVisible = () => {
  const isVisible = state.moveItemsIsVisible
  closeDialogs()
  state.moveItemsIsVisible = !isVisible
}

// more options

const moreOptionsIsVisible = computed(() => userStore.shouldShowMoreAlignOptions)

// connection line collapse/expand

const shouldShowMultipleSelectedLineActions = computed(() => userStore.shouldShowMultipleSelectedLineActions)
const connectionCollapseExpandIsVisible = computed(() => {
  if (onlyConnectionsIsSelected.value) return
  return connectionsIsSelected.value
})

// box collapse/expand

const shouldShowMultipleSelectedBoxActions = computed(() => userStore.shouldShowMultipleSelectedBoxActions)
const boxCollapseExpandIsVisible = computed(() => {
  if (onlyBoxesIsSelected.value) return
  return boxesIsSelected.value
})

// list collapse/expand

const shouldShowMultipleSelectedListActions = computed(() => userStore.shouldShowMultipleSelectedListActions)
const listCollapseExpandIsVisible = computed(() => {
  if (onlyListsIsSelected.value) return
  return listsIsSelected.value
})

// remove

const remove = ({ shouldRemoveCardsOnly }) => {
  const cardIds = editableCards.value.map(card => card.id)
  const connectionIds = editableConnections.value.map(connection => connection.id)
  cardStore.removeCards(cardIds)
  connectionStore.removeConnections(connectionIds)
  if (!shouldRemoveCardsOnly) {
    editableBoxes.value.forEach(box => boxStore.removeBox(box.id))
  }
  if (isSpaceMember.value) {
    lineStore.removeLines(multipleLinesSelectedIds.value)
    listStore.removeLists(multipleListsSelectedIds.value)
  }
  globalStore.closeAllDialogs()
  globalStore.clearMultipleSelected()
}

// dragging dialog

const toggleDraggingDialog = (value) => {
  if (value && state.isPointerDown) {
    globalStore.currentUserIsDraggingMultipleSelectedActionsDialog = value
  } else if (!value) {
    globalStore.currentUserIsDraggingMultipleSelectedActionsDialog = value
    state.isPointerDown = false
  }
}
const toggleIsPointerDown = () => {
  state.isPointerDown = true
}

</script>

<template lang="pug">
dialog.narrow.multiple-selected-actions(
  v-if="visible"
  :open="visible"
  ref="dialogElement"
  @click.left="closeDialogs"
  :style="styles"
  :class="classes"
  @pointermove="toggleDraggingDialog(true)"
  @pointerdown.stop="toggleIsPointerDown"
  @pointerup.stop="toggleDraggingDialog(false)"
)
  .drag-area
  .close-button-wrap.inline-button-wrap(@click="closeAllDialogs")
    button.small-button.inline-button
      img.icon.cancel(src="@/assets/add.svg")
  section(v-if="ItemIsSelected")

    //- Edit Cards
    .row(v-if="cardOrBoxIsSelected")
      //- [·]
      ItemDetailsCheckboxButton(:boxes="boxes" :cards="cards" :isDisabled="!canEditAll.all")
      .segmented-buttons
        //- Connect
        button(v-if="multipleItemsIsSelected" title="Connect/Disconnect Cards" :class="{active: itemsIsConnectedTogether}" @click.left.prevent="toggleConnectItems" @keydown.stop.enter="toggleConnectItems" :disabled="!canEditAll.all")
          img.connect.icon(src="@/assets/connect.svg")
        //- Surround with Box
        button(v-if="cardsIsSelected" title="Surround with Box (B)" @click.left.prevent="containItemsInNewBox" @keydown.stop.enter="containItemsInNewBox" :disabled="!canEditAll.all")
          //- itemsisselected
          img.icon.box-icon(src="@/assets/box.svg")
        //- List
        button(v-if="cardsIsSelected" title="Merge/Split Cards into List (L)" :class="{active: cardsIsInListTogether}" @click.left.prevent="toggleListCards" @keydown.stop.enter="toggleListCards" :disabled="!canEditAll.all")
          img.icon.list-icon(src="@/assets/list.svg")
    //- card options
    CardActions(
      :visible="cardsIsSelected && canEditAll.all"
      :cards="cards"
      @closeDialogs="closeDialogs"
      :backgroundColor="backgroundColor"
      :labelIsVisible="true"
    )
    //- box options
    BoxActions(
      :labelIsVisible="true"
      :visible="(shouldShowMultipleSelectedBoxActions || onlyBoxesIsSelected) && boxesIsSelected"
      :boxes="boxes"
      @closeDialogs="closeDialogs"
      :backgroundColor="backgroundColor"
      :collapseExpandIsVisible="boxCollapseExpandIsVisible"
      @scrollIntoView="scrollIntoView"
    )
    //- connection line options
    ConnectionActions(
      :visible="(shouldShowMultipleSelectedLineActions || onlyConnectionsIsSelected) && connectionsIsSelected"
      :connections="editableConnections"
      @closeDialogs="closeDialogs"
      :canEditAll="canEditAll"
      :backgroundColor="backgroundColor"
      :label="moreLineOptionsLabel"
      :collapseExpandIsVisible="connectionCollapseExpandIsVisible"
      @scrollIntoView="scrollIntoView"
    )
    //- list options
    ListActions(
      :visible="(shouldShowMultipleSelectedListActions || onlyListsIsSelected) && listsIsSelected"
      :lists="lists"
      @closeDialogs="closeDialogs"
      :backgroundColor="backgroundColor"
      label="LIST"
      :collapseExpandIsVisible="listCollapseExpandIsVisible"
      @scrollIntoView="scrollIntoView"
    )

  section
    .row(v-if="cardBoxOrListIsSelected")
      //- Align And Distribute
      AlignAndDistribute(
        :visible="alignableItemsIsSelected"
        :shouldHideMoreOptions="true"
        :shouldDistributeWithAlign="true"
        :canEditAll="canEditAll"
        :cards="cards"
        :editableCards="cards"
        :connections="connections"
        :boxes="boxes"
        :editableBoxes="editableBoxes"
        :lists="lists"
      )
      //- Move/Copy
      .segmented-buttons.move-or-copy-wrap
        button(@click.left.stop="toggleCopyItemsIsVisible" :class="{ active: state.copyItemsIsVisible }")
          span Copy
          MoveOrCopyItems(:visible="state.copyItemsIsVisible" :actionIsMove="false")
        button(@click.left.stop="toggleMoveItemsIsVisible" :class="{ active: state.moveItemsIsVisible }" :disabled="!canEditAll.all")
          span Move
          MoveOrCopyItems(:visible="state.moveItemsIsVisible" :actionIsMove="true")
    //- More Options
    AlignAndDistribute(
      :visible="alignableItemsIsSelected && moreOptionsIsVisible"
      :canEditAll="canEditAll"
      :cards="cards"
      :editableCards="cards"
      :connections="connections"
      :boxes="boxes"
      :editableBoxes="editableBoxes"
      :lists="lists"
    )
    .row
      //- Remove
      button.danger(:disabled="!canEditAll.all" @click.left="remove")
        img.icon(src="@/assets/remove.svg")

      template(v-if="multipleCardsIsSelected")
        //- Merge
        button(@click="mergeSelectedCards" :disabled="!canEditAll.cards")
          img.icon(src="@/assets/merge.svg")
          span Merge
        //- Split
        button(v-if="cardCanBeSplit" @click="splitCard" :disabled="!canEditAll.cards")
          img.icon(src="@/assets/split.svg")
          span Split

    p.badge.info(v-if="canEditAsNonMember && !selectedItemsIsEditableByCurrentUser")
      img.icon.open(src="@/assets/open.svg")
      span In open spaces, you can only edit items you created
</template>

<style lang="stylus">
.multiple-selected-actions
  transform-origin top left
  cursor grab
  touch-action none // touch dragging the dialog shouldn't scroll the page
  &.is-dragging,
  &.is-pointer-down
    cursor grabbing
  .segmented-colors
    display inline-block
    vertical-align middle
    .current-color
      display inline-block
      border-radius 0
      &:first-child
        border-top-left-radius var(--small-entity-radius)
        border-bottom-left-radius var(--small-entity-radius)
      &:last-child
        border-top-right-radius var(--small-entity-radius)
        border-bottom-right-radius var(--small-entity-radius)
        margin-right 0
  .items-checkboxes
    input
      margin 0
    vertical-align 1px
  .align-and-distribute + .move-or-copy-wrap
    margin-left 4px
  .more-options
    margin-bottom 10px

  .button-wrap.disabled
    opacity 0.5
    pointer-events none

  .style-actions + .connection-actions
    border-top 1px solid var(--primary-border)

  .segmented-buttons + .segmented-buttons
    margin-left 0

  .icon.list-icon,
  .icon.connect,
  .icon.box-icon
    vertical-align -0.5px

  &.is-background-light
    section
      border-color var(--primary-border-on-light-background)
    section.subsection + section.subsection
      border-top 1px solid var(--primary-border-on-light-background)
  &.is-background-dark
    section
      border-color var(--primary-border-on-dark-background)
    section.subsection + section.subsection
      border-top 1px solid var(--primary-border-on-dark-background)
  .close-button-wrap,
  .collapse-expand-button-wrap
    cursor pointer
    position absolute
    left initial
    right 0
    top -5px
    padding-right 2px
    padding-left 2px
    padding-bottom 2px
    z-index 1
    button
      cursor pointer
      background-color var(--primary-background)
      &:active,
      &.active
        box-shadow var(--button-active-inset-shadow)
        color var(--primary)
        background-color var(--secondary-active-background)

  .collapse-expand-button-wrap
    right 1px
    .icon.down-arrow
      padding 0
      vertical-align 1px
      padding-left 1px

  .drag-area
    position absolute
    width 100%
    height 8px
    top 0
    left 0
    background-image url('../../assets/drag-area.svg')
    background-repeat: repeat-x
    border-top-left-radius var(--entity-radius)
    border-top-right-radius var(--entity-radius)
    opacity 0.3
  &.is-background-dark
    .drag-area
      background-image url('../../assets/drag-area-invert.svg')
  &.is-dragging
    .drag-area
      opacity 0.6
</style>
